import React, { useState, useEffect } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import ChartTable from "../chart-table";
import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Box,
} from "@mui/material";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const REGION_LIST = [
  { label: "North", value: "NORTH" },
  { label: "South", value: "SOUTH" },
  { label: "East", value: "EAST" },
  { label: "West", value: "WEST" },
  { label: "Central", value: "CENTRAL" },
];

const StateWiseDispatchedReport = ({ data }) => {
  const oemKeys = Object.keys(data || {});

  // Set selectedOem only if oemKeys are present
  const [selectedOem, setSelectedOem] = useState(() => oemKeys[0] || "");
  const [selectedRegion, setSelectedRegion] = useState("");
  const [chartData, setChartData] = useState({});
  const [tableData, setTableData] = useState({ headers: [], rows: [] });

  // Update selectedOem if data changes
  useEffect(() => {
    const keys = Object.keys(data || {});
    if (!selectedOem && keys.length > 0) {
      setSelectedOem(keys[0]);
    }
  }, [data, selectedOem]);

  useEffect(() => {
    const regionsForOem = Object.keys(data[selectedOem] || {});
    if (!regionsForOem.includes(selectedRegion)) {
      setSelectedRegion(regionsForOem[0] || "");
    }
  }, [selectedOem, data]);

  useEffect(() => {
    if (
      !selectedOem ||
      !selectedRegion ||
      !data[selectedOem]?.[selectedRegion]
    ) {
      setChartData({});
      setTableData({ headers: [], rows: [] });
      return;
    }

    const regionData = data[selectedOem][selectedRegion];
    const labels = [];
    const minDays = [];
    const maxDays = [];
    const avgDays = [];

    regionData.forEach((entry) => {
      const state = Object.keys(entry)[0];
      const { min, max, average } = entry[state];
      labels.push(state);
      minDays.push(min);
      maxDays.push(max);
      avgDays.push(average);
    });

    setChartData({
      labels,
      datasets: [
        {
          label: "Min days",
          backgroundColor: "#4a90e2",
          data: minDays,
          barThickness: 30,
        },
        {
          label: "Max days",
          backgroundColor: "#e94e77",
          data: maxDays,
          barThickness: 30,
        },
      ],
    });

    setTableData({
      headers: ["", ...labels],
      rows: [
        ["Min days", ...minDays],
        ["Max days", ...maxDays],
        ["Average", ...avgDays],
      ],
    });
  }, [selectedRegion, selectedOem, data]);

  return (
    <div>
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Box gap={2} mb={2} sx={{ display: "flex", width: "50%" }}>
          <FormControl fullWidth size="small">
            <InputLabel>Region</InputLabel>
            <Select
              value={selectedRegion || ""}
              onChange={(e) => setSelectedRegion(e.target.value)}
              label="Region"
              disabled={
                !data[selectedOem] ||
                Object.keys(data[selectedOem]).length === 0
              }
            >
              {REGION_LIST.filter(
                (region) => data[selectedOem] && data[selectedOem][region.value]
              ).map((region) => (
                <MenuItem key={region.value} value={region.value}>
                  {region.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>OEM</InputLabel>
            <Select
              value={selectedOem || ""}
              onChange={(e) => setSelectedOem(e.target.value)}
              label="OEM"
            >
              {oemKeys.map((oem) => (
                <MenuItem key={oem} value={oem}>
                  {oem}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>
      {!chartData?.labels?.length ? (
        <Typography variant="body1" color="text.secondary">
          No data available for the selected OEM and region.
        </Typography>
      ) : (
        <>
          <Box sx={{ height: 350, mb: 2 }}>
            <Bar
              data={chartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: "top",
                  },
                  datalabels: {
                    color: "#ffffff",
                    anchor: "center",
                    align: "center",
                    font: {
                      weight: "bold",
                    },
                  },
                },
              }}
            />
          </Box>
          <ChartTable headers={tableData.headers} rows={tableData.rows} />
        </>
      )}
    </div>
  );
};

export default StateWiseDispatchedReport;
